import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Args = {
  bankAccountIds: string[];
  dateFrom: string;
  dateTo: string;
  enabled?: boolean;
};

export type TransferDayRow = {
  transfer_out: number; // from_amount (debit from source account)
  transfer_in: number;  // to_amount (credit to destination account)
  currency: string | null;
};

export type InternalTransfersMatrix = {
  dateKeys: string[];
  byAccount: Map<string, Map<string, TransferDayRow>>;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

function buildDateKeys(dateFrom: string, dateTo: string): string[] {
  const start = new Date(`${dateFrom}T00:00:00Z`);
  const end = new Date(`${dateTo}T00:00:00Z`);
  const keys: string[] = [];
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    keys.push(`${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`);
  }
  return keys;
}

export function useInternalTransfersMatrixRange(args: Args) {
  const { bankAccountIds, dateFrom, dateTo, enabled = true } = args;

  return useQuery({
    queryKey: ["internal_transfers_matrix", bankAccountIds, dateFrom, dateTo],
    enabled: enabled && bankAccountIds.length > 0 && !!dateFrom && !!dateTo,
    queryFn: async (): Promise<InternalTransfersMatrix> => {
      const dateKeys = buildDateKeys(dateFrom, dateTo);

      const { data, error } = await supabase
        .from("internal_transfers" as any)
        .select("transfer_date, from_account_id, to_account_id, from_amount, to_amount, from_currency, to_currency")
        .gte("transfer_date", dateFrom)
        .lte("transfer_date", dateTo)
        .or(`from_account_id.in.(${bankAccountIds.join(",")}),to_account_id.in.(${bankAccountIds.join(",")})`)

      if (error) throw error;

      const byAccount = new Map<string, Map<string, TransferDayRow>>();
      for (const id of bankAccountIds) {
        byAccount.set(id, new Map());
      }

      for (const tx of (data ?? []) as any[]) {
        const dayKey = String(tx.transfer_date).slice(0, 10);
        const fromId = String(tx.from_account_id);
        const toId = String(tx.to_account_id);
        const fromAmount = Number(tx.from_amount) || 0;
        const toAmount = Number(tx.to_amount) || 0;

        // Source account: debit (money going out)
        if (bankAccountIds.includes(fromId)) {
          const accMap = byAccount.get(fromId)!;
          const row = accMap.get(dayKey) ?? { transfer_out: 0, transfer_in: 0, currency: tx.from_currency ?? null };
          row.transfer_out += fromAmount;
          accMap.set(dayKey, row);
        }

        // Destination account: credit (money coming in)
        if (bankAccountIds.includes(toId)) {
          const accMap = byAccount.get(toId)!;
          const row = accMap.get(dayKey) ?? { transfer_out: 0, transfer_in: 0, currency: tx.to_currency ?? null };
          row.transfer_in += toAmount;
          accMap.set(dayKey, row);
        }
      }

      return { dateKeys, byAccount };
    },
    staleTime: 15_000,
  });
}
